import { batchRequestsOf, filterObject } from '@repo/utils';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { LOADING_KEY, withLoading } from 'utils/promise';
import { createThrottledRequest } from 'utils/time';

import { queryClient } from 'components/provider/GlobalProvider';

import http from '../http';
import { REVENUES_KEY } from './revenue';

export const WITHDRAWAL_KEY = {
  all: ['withdrawal'],
  histories: () => [...WITHDRAWAL_KEY.all, 'history'],
  history: <T>(filter: T) => [...WITHDRAWAL_KEY.histories(), filter],
  infos: () => [...WITHDRAWAL_KEY.all, 'info'],
  info: <T>(filter: T) => [...WITHDRAWAL_KEY.infos(), filter],
};

export interface WithdrawalsHistoryResponseDto {
  id: string;
  requestedAmount: number;
  requestedAmountFormat: string;
  actualAmount: number;
  actualAmountFormat: string;
  status: string;
  cancelable: boolean;
  createdAt: string;
}

export interface WithdrawalsHistorySearchParamsDto {
  startAt?: string;
  endAt?: string;
  limit: number;
}

export const useGetWithdrawalsHistory = (queryParams: WithdrawalsHistorySearchParamsDto) => {
  const _queryParams = filterObject<object>(queryParams);
  return useQuery(WITHDRAWAL_KEY.histories(), () =>
    http.get<WithdrawalsHistoryResponseDto[]>('/affiliate/v1/withdrawals/history', _queryParams)
  );
};

export interface WithdrawalsInfoResponseDto {
  availableWithdrawal: number;
  availableWithdrawalFormat: string;
  minAmount: number;
  maxAmount: number;
  minAmountFormat: string;
  maxAmountFormat: string;
  availablePreTaxWithdrawal: number;
  availablePreTaxWithdrawalFormat: string;
  availableAfterTaxWithdrawal: number;
  availableAfterTaxWithdrawalFormat: string;
  minBubbleRewardAmount: number;
  minBubbleRewardAmountFormat: string;
  maxBubbleRewardAmount: number;
  maxBubbleRewardAmountFormat: string;
}

const fetchWithdrawalsInfo = async () => {
  try {
    return await http.get<WithdrawalsInfoResponseDto>('/affiliate/v1/withdrawals/info');
  } catch (error) {
    return null;
  }
};

export const prefetchWithdrawalsInfo = async () => {
  try {
    await queryClient.prefetchQuery({
      queryKey: WITHDRAWAL_KEY.infos(),
      queryFn: fetchWithdrawalsInfo,
      staleTime: 1000 * 2,
    });
  } catch (error) {
    console.error('Prefetch Error', error);
  }
};

export const useGetWithdrawalsInfo = (type: 'bubble' | 'cash') => {
  const queryClient = useQueryClient();
  const hasPrefetchingData = queryClient.getQueryData(WITHDRAWAL_KEY.infos());
  const queryCache = queryClient.getQueryCache().find(WITHDRAWAL_KEY.infos());

  let isStale = false;
  if (queryCache) {
    isStale = Date.now() - queryCache.state.dataUpdatedAt > 2000;
  }

  return useQuery(
    WITHDRAWAL_KEY.infos(),
    () => http.get<WithdrawalsInfoResponseDto>('/affiliate/v1/withdrawals/info'),
    {
      enabled: isStale || !hasPrefetchingData, // 데이터가 없거나 상태가 stale이면 데이터를 가져온다.
      select: (data) => {
        if (type === 'bubble') {
          return {
            ...data,
            // minAmount: data?.minAmount ?? 0,
            // maxAmount: data?.maxAmount ?? 0,
            minAmount: data?.minBubbleRewardAmount ?? 0,
            maxAmount: data?.maxBubbleRewardAmount ?? 0,
            minAmountFormat: data?.minBubbleRewardAmountFormat ?? '0',
            maxAmountFormat: data?.maxBubbleRewardAmountFormat ?? '0',
            availableAfterTaxWithdrawal: data?.availableAfterTaxWithdrawal ?? 0,
            availableAfterTaxWithdrawalFormat: data?.availableAfterTaxWithdrawalFormat ?? '0',
            availablePreTaxWithdrawal: data?.availablePreTaxWithdrawal ?? 0,
            availablePreTaxWithdrawalFormat: data?.availablePreTaxWithdrawalFormat ?? '0',
            availableWithdrawal: data?.availableWithdrawal ?? 0,
            availableWithdrawalFormat: data?.availableWithdrawalFormat ?? '0',
            minBubbleRewardAmount: data?.minBubbleRewardAmount ?? 0,
            minBubbleRewardAmountFormat: data?.minBubbleRewardAmountFormat ?? '0',
            maxBubbleRewardAmount: data?.maxBubbleRewardAmount ?? 0,
            maxBubbleRewardAmountFormat: data?.maxBubbleRewardAmountFormat ?? '0',
          };
        } else if (type === 'cash') {
          return {
            ...data,
            minAmount: data?.minAmount ?? 0,
            maxAmount: data?.maxAmount ?? 0,
            minAmountFormat: data?.minAmountFormat ?? '0',
            maxAmountFormat: data?.maxAmountFormat ?? '0',
            availableAfterTaxWithdrawal: data?.availableAfterTaxWithdrawal ?? 0,
            availableAfterTaxWithdrawalFormat: data?.availableAfterTaxWithdrawalFormat ?? '0',
            availablePreTaxWithdrawal: data?.availablePreTaxWithdrawal ?? 0,
            availablePreTaxWithdrawalFormat: data?.availablePreTaxWithdrawalFormat ?? '0',
            availableWithdrawal: data?.availableWithdrawal ?? 0,
            availableWithdrawalFormat: data?.availableWithdrawalFormat ?? '0',
            minBubbleRewardAmount: data?.minBubbleRewardAmount ?? 0,
            minBubbleRewardAmountFormat: data?.minBubbleRewardAmountFormat ?? '0',
            maxBubbleRewardAmount: data?.maxBubbleRewardAmount ?? 0,
            maxBubbleRewardAmountFormat: data?.maxBubbleRewardAmountFormat ?? '0',
          };
        }
      },
    }
  );
};

export type WithdrawalsRequestBodyDto =
  | {
      preTaxAmount: number;
      afterTaxAmount?: undefined;
      type: 'bubble-reward' | 'cash-reward';
    }
  | {
      preTaxAmount?: undefined;
      afterTaxAmount: number;
      type: 'bubble-reward' | 'cash-reward';
    };

const postWithdrawalsRequest = batchRequestsOf(
  createThrottledRequest(
    async (dto: WithdrawalsRequestBodyDto) => {
      return http.post<WithdrawalsHistoryResponseDto>('/affiliate/v1/withdrawals/request', dto);
    },
    2000,
    {
      leading: true,
      trailing: false,
    }
  )
);
// (dto: WithdrawalsRequestBodyDto) => withLoading(LOADING_KEY.WITHDRAWAL_REQUEST, () => postWithdrawalsRequest(dto)),
// postWithdrawalsRequest;
export const usePostWithdrawalsRequest = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (dto: WithdrawalsRequestBodyDto) => withLoading(LOADING_KEY.WITHDRAWAL_REQUEST, () => postWithdrawalsRequest(dto)),
    {
      onSuccess: async () => {
        // 출금 정보 및 수익 정보를 초기화한다.
        await Promise.all([
          queryClient.invalidateQueries(WITHDRAWAL_KEY.histories()),
          queryClient.invalidateQueries(WITHDRAWAL_KEY.infos()),
          queryClient.invalidateQueries(REVENUES_KEY.lists()),
        ]);
      },
    }
  );
};

// export interface ExchangeBubbleRequestBodyDto {
//   amount: number;
// }
// interface ExchangeBubbleResponseDto {}

// const postChangeBubbleRequest = batchRequestsOf(
//   createThrottledRequest(
//     async (dto: ExchangeBubbleRequestBodyDto) => {
//       return http.post<ExchangeBubbleResponseDto>('/affiliate/v1/withdrawals/bubble-request', dto);
//     },
//     2000,
//     {
//       leading: true,
//       trailing: false,
//     }
//   )
// );

// export const usePostChangeBubbleRequest = () => {
//   const queryClient = useQueryClient();
//   return useMutation(postChangeBubbleRequest, {
//     onSuccess: async () => {
//       // 출금 정보 및 수익 정보를 초기화한다.
//       await Promise.all([
//         queryClient.invalidateQueries(WITHDRAWAL_KEY.histories()),
//         queryClient.invalidateQueries(WITHDRAWAL_KEY.infos()),
//         queryClient.invalidateQueries(REVENUES_KEY.lists()),
//       ]);
//     },
//   });
// };
